import React from "react";
import { AlertCircle } from "lucide-react";
import { safeString } from "../../utils/funcs";
import {
  // clearStorage,
  ConsultationRoomErrorContent,
  DefaultErrorContent,
  DoctorAddRecommendationErrorContent,
  WaitingRoomErrorContent,
} from "./router-error-boundaries";
import { logException } from "../../libs/sentry";
import { phCapture, phCaptureException, PH } from "../../libs/posthog";

type ErrorLocation =
  | "conclude"
  | "consultation-room"
  | "waiting-room"
  | "doctor-waiting-room"
  | "other";

type UserType = "doctor" | "patient";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorLocation: ErrorLocation | null;
  userType: UserType | null;
  consultationId: string | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static instance: ErrorBoundary | null = null;

  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorLocation: null,
    userType: null,
    consultationId: null,
  };

  componentDidMount(): void {
    ErrorBoundary.instance = this;
  }

  componentWillUnmount(): void {
    if (ErrorBoundary.instance === this) {
      ErrorBoundary.instance = null;
    }
  }

  static getDerivedStateFromError(
    error: Error,
  ): Pick<ErrorBoundaryState, "hasError"> {
    // AbortErrors are benign: Apollo cancels in-flight requests when a component
    // unmounts during navigation. Swallowing them prevents a false error screen.
    const isAbortError =
      error instanceof DOMException && error.name === "AbortError";
    return { hasError: !isAbortError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const isAbortError =
      error instanceof DOMException && error.name === "AbortError";
    if (isAbortError) return;
    this.handleError(error, errorInfo);
  }

  resolveUserTypeFromPath = (path: string) => {
    if (path.includes("/doctor/")) {
      return "doctor";
    } else {
      return "patient";
    }
  };

  determineErrorLocation(path: string) {
    // Check for conclude consultation page
    if (path.includes("/doctor/conclude-consultation/")) {
      return "conclude";
    }

    // Check for consultation room pages (doctor or patient)
    if (
      path.includes("/doctor/chat/") ||
      path.includes("/doctor/video/") ||
      path.includes("/doctor/voice/") ||
      path.match(/\/chat\/[^/]+$/) ||
      path.match(/\/video\/[^/]+$/) ||
      path.match(/\/voice\/[^/]+$/)
    ) {
      return "consultation-room";
    }

    // Check for waiting room (just consultationId at the end)
    if (path.match(/\/[^/]+$/) && !path.includes("/doctor/")) {
      return "waiting-room";
    }

    if (
      path.match(/\/doctor\/[^/]+$/) &&
      !path.includes("/chat") &&
      !path.includes("/video")
    ) {
      return "doctor-waiting-room";
    }

    return "other";
  }

  getConsultationIdFromPath(path: string) {
    // Handle doctor routes with consultationId
    const doctorConsultationMatch = path.match(
      /\/doctor\/(?:video|voice|waiting|conclude-communication|conclude-consultation|review-recommendations)\/([^/]+)$/,
    );
    if (doctorConsultationMatch && doctorConsultationMatch[1]) {
      return doctorConsultationMatch[1];
    }

    // Handle doctor waiting room: /doctor/:consultationId
    const doctorWaitingMatch = path.match(/\/doctor\/([^/]+)$/);
    if (doctorWaitingMatch && doctorWaitingMatch[1]) {
      return doctorWaitingMatch[1];
    }

    // Handle chat routes (both patient and doctor): /chat/:roomId or /doctor/chat/:roomId
    // Extract consultationId from roomId (assumes format: consultationId_otherId)
    const chatMatch = path.match(/\/(?:doctor\/)?chat\/([^/]+)$/);
    if (chatMatch && chatMatch[1]) {
      return chatMatch[1].split("_")[0];
    }

    // Handle video/voice routes: /video/:consultationId or /voice/:consultationId
    const mediaMatch = path.match(/\/(?:video|voice)\/([^/]+)$/);
    if (mediaMatch && mediaMatch[1]) {
      return mediaMatch[1];
    }

    // Handle rate consultation: /rate-consultation/:consultationId
    const rateMatch = path.match(/\/rate-consultation\/([^/]+)$/);
    if (rateMatch && rateMatch[1]) {
      return rateMatch[1];
    }

    // Handle retry: /retry/:consultationId
    const retryMatch = path.match(/\/retry\/([^/]+)$/);
    if (retryMatch && retryMatch[1]) {
      return retryMatch[1];
    }

    // Handle patient waiting room: /:consultationId (catch-all for root-level paths)
    const waitingMatch = path.match(/^\/([^/]+)$/);
    if (waitingMatch && waitingMatch[1] && waitingMatch[1] !== "success") {
      return waitingMatch[1];
    }

    return null;
  }

  handleError(error: Error, errorInfo?: React.ErrorInfo): void {
    const currentPath = window?.location?.pathname;
    const errorLocation = this.determineErrorLocation(currentPath);
    const userType = this.resolveUserTypeFromPath(currentPath);
    const consultationId = this.getConsultationIdFromPath(currentPath);

    logException(
      error,
      safeString(consultationId),
      userType,
      "app-crashed-error-boundary",
      errorLocation,
    );
    phCaptureException(error, {
      consultationId,
      userType,
      errorLocation,
      componentStack: errorInfo?.componentStack,
    });
    phCapture(PH.ERROR_BOUNDARY_TRIGGERED, {
      errorMessage: error.message,
      consultationId,
      userType,
      errorLocation,
    });

    const fallbackErrorInfo: React.ErrorInfo = errorInfo || {
      componentStack: error.stack || "",
    };

    this.setState({
      hasError: true,
      error,
      errorInfo: fallbackErrorInfo,
      errorLocation,
      userType,
      consultationId,
    });

    // Log error to console for debugging
    console.error("Error caught by boundary:", error, fallbackErrorInfo);
  }

  getErrorContent = (props: ErrorBoundaryState): React.ReactNode => {
    const contents: Partial<Record<ErrorLocation, React.ReactNode>> = {
      "waiting-room": <WaitingRoomErrorContent />,
      "doctor-waiting-room": <WaitingRoomErrorContent />,
      "consultation-room": <ConsultationRoomErrorContent {...props} />,
      conclude: <DoctorAddRecommendationErrorContent {...props} />,
      other: <DefaultErrorContent />,
    };
    const location = props.errorLocation ?? "other";
    return contents[location] || <DefaultErrorContent />;
  };

  renderErrorContent(): React.ReactNode {
    return this.getErrorContent(this.state);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryContainer
          header="Something Went Wrong"
          info={safeString(this.state.error?.message)}
        >
          <>
            {this.renderErrorContent()}

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-6 p-4 bg-background rounded-lg">
                <summary className="cursor-pointer font-medium text-foreground mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs text-foreground-muted overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </>
        </ErrorBoundaryContainer>
      );
    }

    return this.props.children;
  }
}

export const handleGlobalError = (
  error: Error,
  errorInfo?: React.ErrorInfo,
): void => {
  const instance = ErrorBoundary.instance;
  if (instance) {
    instance.handleError(error, errorInfo);
  } else {
    console.error("Global error caught but ErrorBoundary not mounted:", error);
  }
};

export const ErrorBoundaryContainer = ({
  children,
  header,
}: // info,
{
  children: React.ReactNode;
  header: string;
  info?: string;
}) => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl shadow-level-1 p-8 max-w-md w-full">
        <div className="flex flex-col items-center gap-3 mb-6">
          <div className="bg-error-light p-3 rounded-full">
            <AlertCircle className="text-error" size={24} />
          </div>
          <h1 className="type-h2 text-foreground">{header}</h1>
        </div>
        {children}
      </div>
    </div>
  );
};
